"use client";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { VerifiedOutline } from "@/components/icons/verified-outline";
import { useUserKYC } from "@/hooks/useUserKYC";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  closeDialog,
  openKycPlatinumLevelDialog,
  openKycStandardLevelDialog,
  selectCurrentDialog,
  setKycNextLevelRequirements,
  setUserId,
} from "@/store/profileSlice";
import { cn } from "@/lib/utils";
import { useUser } from "@/hooks/useUser";
import { Button } from "@/components/ui/button";
import { useKYCNextStepRequirements } from "@/hooks/useKYCNextStepRequirements";
import { useEffect } from "react";
import { getDocumentLabel } from "../kyc-standard-upgrade";
import { useRouter } from "next/navigation";

const REQUIREMENTS = {
  NIGERIA: {
    standard: ["ng_bvn"],
    platinum: ["ng_nin", "ng_vnin"],
  },
  GHANA: {
    standard: [
      "gh_ssnit",
      "gh_passport",
      "gh_voters_card",
      "gh_drivers_license",
    ],
    platinum: [
      "gh_ssnit",
      "gh_passport",
      "gh_voters_card",
      "gh_drivers_license",
    ],
  },
} as const;

export function KycDialogContent() {
  const isMobile = useMediaQuery("(max-width:768px)");
  const dialog = useAppSelector(selectCurrentDialog);
  const { data: user } = useUser();
  const dispatch = useAppDispatch();
  const router = useRouter();
  
  const { data: currentKYC } = useUserKYC({
    queryParams: {
      user: user?.id, 
    },
  });

   const { data: nextLevelRequirements } = useKYCNextStepRequirements({
    queryParams: { user: user?.id },
  });

  useEffect(() => {
    dispatch(
      setKycNextLevelRequirements(nextLevelRequirements.requirements?.[0]),
    );
  }, [nextLevelRequirements, dispatch]);

  const currentTier = currentKYC?.data?.current_tier;

  const hasBasicRequirements = Boolean(
    user?.email && user?.firstName && user?.lastName,
  );

  const hasStandardKYC =
    currentTier === "standard" || currentTier === "platinum";
  const hasBusinessProfile = Boolean(user?.businessProfile);

  const canUpgradeToStandard = hasBasicRequirements && hasBusinessProfile && !hasStandardKYC;

  const hasPlatinumKYC = currentTier === "platinum";
  const canUpgradeToPlatinum = hasStandardKYC && !hasPlatinumKYC;

  const handleOpenStandardKyc = () => {
    if (user?.id) {
      dispatch(setUserId(user.id)); 
      dispatch(openKycStandardLevelDialog());
    }
  };

  const handleOpenPlatinumKyc = () => {
    if (user?.id) {
      dispatch(setUserId(user.id)); 
      dispatch(openKycPlatinumLevelDialog());
    }
  };

  return (
    <Sheet
      open={dialog === "kycDetails"}
      onOpenChange={(val) => {
        if (!val) {
          dispatch(closeDialog());
        }
      }}
    >
      <SheetContent
        className="md:max-w-[500px] font-outfit px-4 lg:px-6 rounded-t-xl md:rounded-t-none overflow-y-scroll max-h-[70vh] md:max-h-screen"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        side={isMobile ? "bottom" : "right"}
      >
        <SheetHeader>
          <SheetTitle className="text-center pb-10">KYC</SheetTitle>
          <SheetDescription className="sr-only">
            Upgrade your KYC
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-10">
          {/* Basic Wallet (Tier 1) */}
          <div className="flex flex-col gap-4 bg-primary-50/40 p-4 rounded-lg">
            <h3>Basic Wallet Requirements</h3>
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(
                    user?.firstName && user?.lastName && "text-secondary-500",
                  )}
                />
                <span>Legal Name</span>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(user?.email && "text-secondary-500")}
                />
                <span>Email Address</span>
              </div>
            </div>
            <div>
              <h4>Limits</h4>
              <p className="text-primary-500">
                Maximum Payout Amount:{" "}
                <strong>
                  {new Intl.NumberFormat("zh-CN", {
                    style: "currency",
                    currency: "CNY",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(1000)}
                </strong>
              </p>
            </div>
          </div>

          {/* Standard Wallet (Tier 2) */}
          <div className="flex flex-col gap-4 border border-border-300 p-4 rounded-lg">
            <h3>Standard Wallet Requirements</h3>
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(hasBasicRequirements && "text-secondary-500")}
                />
                <span>All tier 1 requirements</span>
              </div>
              <div className="flex flex-row gap-2 items-center justify-between">
                <div className="flex items-center gap-2">
                  <VerifiedOutline
                    className={cn(hasBusinessProfile && "text-secondary-500")}
                  />
                  <span>Complete business information and profile image</span>
                </div>

                {!hasBusinessProfile && (
                  <Button
                    size="xs"
                    radius="full"
                    variant="secondary"
                    className="bg-white border border-secondary-500"
                    onClick={() => {
                      dispatch(closeDialog());
                      router.push("/user/profile");
                    }}
                  >
                    Complete
                  </Button>
                )}
              </div>
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(hasStandardKYC && "text-secondary-500")}
                />
                <span>
                  {REQUIREMENTS[user?.country]?.standard
                    ?.map((req) => getDocumentLabel(req))
                    .join(", ")}
                </span>
                {!hasStandardKYC && canUpgradeToStandard && (  
                  <Button
                    className="bg-white border border-secondary-500"
                    size="xs"
                    radius="full"
                    variant="secondary"
                    onClick={handleOpenStandardKyc}
                  >
                    Verify
                  </Button>
                )}
              </div>
            </div>
            <div>
              <h4>Limits</h4>
              <p className="text-primary-500">
                Maximum Payout Amount:{" "}
                <strong>
                  {new Intl.NumberFormat("zh-CN", {
                    style: "currency",
                    currency: "CNY",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(2500)}
                </strong>
              </p>
            </div>
          </div>

          {/* Platinum Wallet (Tier 3) */}
          <div className="flex flex-col gap-4 p-4 rounded-lg border border-border-300">
            <h3>Platinum Wallet Requirements</h3>
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(hasStandardKYC && "text-secondary-500")}
                />
                <span>All tier 2 requirements</span>
              </div>
              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(hasPlatinumKYC && "text-secondary-500")}
                />
                <span>
                  {REQUIREMENTS[user?.country]?.platinum
                    ?.map((req) => getDocumentLabel(req))
                    .join(", ")}
                </span>
                {!hasPlatinumKYC && canUpgradeToPlatinum && (
                  <Button
                    className="bg-white border border-secondary-500"
                    size="xs"
                    radius="full"
                    variant="secondary"
                    onClick={handleOpenPlatinumKyc}
                  >
                    Verify
                  </Button>
                )}
              </div>

              <div className="flex flex-row gap-2 items-center">
                <VerifiedOutline
                  className={cn(hasPlatinumKYC && "text-secondary-500")}
                />
                <span>Facial verification</span>
              </div>
            </div>
            <div>
              <h4>Limits</h4>
              <p className="text-primary-500">
                Maximum Payout Amount: <strong>Unlimited</strong>
              </p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
